import { useRef, useState, type CSSProperties, type ReactNode } from 'react';
import {
  BALL_VALUES,
  COLORS,
  type AuthResult,
  type BallColor,
  type Visit,
  type VisitEntry,
  type ScoreState,
} from '@aiscoreboard/contracts';
import { createPracticeMatch, endMatch, getMatch, sendEvent } from '../lib/api';
import { useMatch } from '../lib/useMatch';
import { useMatchDevices } from '../lib/useMatchDevices';
import { useYouTubeBroadcast } from '../lib/useYouTubeBroadcast';
import { practicePots } from '../lib/practice';
import { isCameraLicenseExpired } from '../lib/camera';
import { assertCameraFeature, CameraControlError, cameraControlTarget, changeCameraMode, changeCameraStreaming } from '../lib/cameraControl';
import { AiNotice, ControlBar } from '../components/ControlBar';
import { FitLabel } from '../components/ActionBar';
import { finishPractice, startPractice } from '../lib/accounts';
import { BrandMark } from '../components/BrandMark';
import { BreakHistory } from '../components/BreakHistory';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Countdown } from '../components/Countdown';
import { FullscreenToggle } from '../components/FullscreenToggle';
import { CrownIcon, TrophyIcon, UndoIcon } from '../components/Icons';
import { SeatCard } from '../components/SeatCard';
import { TimeLimitField } from '../components/TimeLimitField';
import { BALL_GLOSS, BALL_SWATCH, PALE_BALLS, ballKey } from '../lib/balls';
import { accentOf, frameVars, QUIET_FRAME } from '../lib/accent';
import { useT } from '../lib/i18n';
import type { SeatSession } from '../lib/useSeats';

interface Props {
  /** The seat this player occupies, or `null` for a guest with nothing to save to. */
  session: SeatSession | null;
  name: string;
  onSignIn: () => void;
  onSignOut: () => void;
  onSettings: () => void;
  /** A phone claimed the QR code on this screen and signed in there. */
  onPaired: (auth: AuthResult) => void;
  onBack: () => void;
}

/**
 * Solo practice.
 *
 * Manual practice stays local. Enabling a camera migrates the pot history
 * to a solo event log shared by manual scoring, AI and the stream overlay.
 *
 * A guest can practise too. Nothing is recorded for them, which is said
 * plainly rather than discovered at the end, but counting your own break is
 * not worth making an account for.
 *
 * Setting a session up is the match setup screen with one seat instead of
 * two: the same card, the same QR code, the same clock. It used to be a bare
 * list with a sign-in button and no way to scan, which meant the one player
 * who most needs their session saved — a lone player with a phone in their
 * pocket and no keyboard — was the only one who had to type a password on a
 * shared tablet.
 */
export function PracticeScreen({
  session,
  name,
  onSignIn,
  onSignOut,
  onSettings,
  onPaired,
  onBack,
}: Props) {
  const t = useT();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiNotice, setAiNotice] = useState<string | null>(null);
  const [leaving, setLeaving] = useState(false);
  const token = session?.token ?? null;

  /** Optional cap on the session, set before it starts, as a match has. */
  const [timeLimitMinutes, setTimeLimitMinutes] = useState<number | null>(null);
  const [deadline, setDeadline] = useState<Date | null>(null);

  /** Every pot in order, so undo can rebuild the breaks exactly. */
  const [localPots, setPots] = useState<(BallColor | 'end')[]>([]);
  const [backing, setBacking] = useState<ScoreState | null>(null);
  const backingId = useRef<string | null>(null);
  const remote = useMatch(backing?.matchId ?? null, false);
  const youtube = useYouTubeBroadcast(backing?.matchId ?? null);
  const cameras = useMatchDevices(backing?.matchId ?? null);
  const [busy, setBusy] = useState(false);
  const operating = useRef(false);
  const state = remote.state && backing && remote.state.matchId === backing.matchId
    && remote.state.lastSeq >= backing.lastSeq ? remote.state : backing;
  const pots = state ? practicePots(state.visits) : localPots;
  const expired = cameras?.data?.devices.some(isCameraLicenseExpired) ?? false;
  const locked = busy || remote.saving;
  const scoringDisabled = locked || (backing !== null && (!remote.state || state?.status !== 'in_progress'));
  const [saved, setSaved] = useState<{ points: number; highestBreak: number } | null>(null);

  const breaks = toBreaks(pots);
  const current = currentBreak(pots);
  const visits = toVisits(pots);
  const accent = accentOf(0);
  const pointsScored = breaks.reduce((a, b) => a + b, 0) + current;
  const highestBreak = Math.max(0, ...breaks, current);

  const cameraChange = async (feature: 'streaming' | 'ai', enabled: boolean): Promise<void> => {
    if (operating.current || remote.saving || state?.status === 'match_over') return;
    operating.current = true;
    setBusy(true);
    setError(null);
    try {
      const target = await cameraControlTarget(backingId.current ?? undefined, enabled);
      if (enabled) assertCameraFeature(target.device, feature === 'ai');
      let id = backingId.current;
      if (!id) {
        const created = await createPracticeMatch(name, localPots, target.paired || !target.token ? undefined : target.device.venueId);
        id = created.id;
        backingId.current = id;
        setBacking(created.state);
      }
      const matchId = id;
      const dispatch = async (event: Parameters<typeof sendEvent>[1]): Promise<boolean> => {
        const result = await sendEvent(matchId, event);
        setBacking(result.state);
        return true;
      };
      if (feature === 'streaming') await changeCameraStreaming(target, id, enabled, dispatch);
      else await changeCameraMode(target, id, enabled ? 'assisted' : 'manual', dispatch);
    } catch (e) {
      if (feature === 'ai' && enabled && e instanceof CameraControlError
        && (e.key === 'match.aiUnsupported' || e.key === 'match.licenseExpired')) {
        setAiNotice(t(e.key));
      } else {
        setError(e instanceof CameraControlError ? t(e.key) : e instanceof Error ? e.message : String(e));
      }
    } finally {
      operating.current = false;
      setBusy(false);
    }
  };

  const stopCameraSession = async (): Promise<(BallColor | 'end')[]> => {
    if (!backingId.current) return localPots;
    const snapshot = await getMatch(backingId.current);
    const ended = snapshot.state.status === 'match_over'
      ? snapshot.state : (await endMatch(backingId.current, 0)).state;
    setBacking(ended);
    return practicePots(ended.visits);
  };

  const leave = async (): Promise<void> => {
    if (operating.current || remote.saving) return;
    operating.current = true;
    setBusy(true);
    setLeaving(false);
    try {
      await stopCameraSession();
      onBack();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      operating.current = false;
      setBusy(false);
    }
  };

  const begin = async (): Promise<void> => {
    setStarting(true);
    setError(null);
    setSaved(null);
    try {
      // A guest session exists only in this tab, so it needs no server round
      // trip and no id from one.
      const id = token === null ? 'guest' : (await startPractice(token)).id;
      setPots([]);
      setDeadline(
        timeLimitMinutes === null ? null : new Date(Date.now() + timeLimitMinutes * 60_000),
      );
      setSessionId(id);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('practice.startFailed'));
    } finally {
      setStarting(false);
    }
  };

  const finish = async (): Promise<void> => {
    if (!sessionId || operating.current || remote.saving) return;
    operating.current = true;
    setBusy(true);
    setError(null);
    try {
      const finalPots = await stopCameraSession();
      const all = toBreaks(finalPots);
      const last = currentBreak(finalPots);
      if (last > 0) all.push(last);
      const points = all.reduce((a, b) => a + b, 0);
      const best = Math.max(0, ...all);
      if (token !== null) await finishPractice(token, sessionId, {
        pointsScored: points,
        ballsPotted: finalPots.filter(pot => pot !== 'end').length,
        highestBreak: best,
        breaks: all,
      });
      setSaved({ points, highestBreak: best });
      setSessionId(null);
      setDeadline(null);
      setPots([]);
      backingId.current = null;
      setBacking(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('practice.saveFailed'));
    } finally {
      operating.current = false;
      setBusy(false);
    }
  };

  if (!sessionId) {
    return (
      // Same layout language as setup/scoring, with no fixed aspect ratio.
      <div className="board-viewport">
        <main
          className="board-shell setup-shell mx-auto flex w-full max-w-[min(140rem,95vw)] flex-col gap-2 p-2 sm:gap-3 sm:p-3"
        >
          <header className="setup-header grid shrink-0 grid-cols-[1fr_auto_1fr] items-center gap-2 md:gap-3">
            <div className="flex min-w-0 items-center gap-2">
              <button
                type="button"
                onClick={onBack}
                className="btn-ghost shrink-0 px-3.5 py-2 text-[0.78rem] font-semibold tracking-[0.04em]"
              >
                ← {t('common.back')}
              </button>
              <h1 className="truncate text-xl font-black">{t('practice.title')}</h1>
            </div>
            <BrandMark compact className="text-center" />
            <div className="flex min-w-0 items-center justify-end">
              <FullscreenToggle className="btn-ghost px-3 py-2 text-[0.72rem] font-bold uppercase tracking-[0.12em] text-white/85" />
            </div>
          </header>

          {/* One seat with the clock stacked beneath it, rather than the
              match's seat-settings-seat. Both boxes are held to the same
              width and the same centred column as the start button below, so
              the seat card, the clock and the button read as one form being
              filled in from top to bottom rather than as panels arranged
              around a room.

              The column is narrow — a little over a third of the screen on a
              venue display. One player has one seat to fill in, and a form
              stretched across a sixteen-by-ten board puts a sign-in button a
              foot wide under a code the size of a beer mat. The code shrinks
              with it: it is scanned from a phone held a foot away, and the
              square only has to fit the card. */}
          <div className="flex flex-1 justify-center">
            <div className="practice-setup flex w-full max-w-[clamp(28.2rem,42vw,36rem)] flex-col items-stretch gap-2 sm:gap-3">
              <SeatCard
                label={t('practice.player')}
                accent={accentOf(0)}
                session={session}
                onSignIn={onSignIn}
                onSignOut={onSignOut}
                onSettings={onSettings}
                onPaired={onPaired}
                codeSize={201}
              />

              <div className="panel w-full shrink-0 px-3 py-2">
                <TimeLimitField
                  value={timeLimitMinutes}
                  onChange={setTimeLimitMinutes}
                  hint={t('practice.clockHint')}
                  dense
                />
              </div>

              {/* The last session's result, and only once there is one. An
                  empty box reserved for a number that has never been scored is
                  a hole in the page. */}
              {saved && (
                <div className="panel w-full shrink-0 px-3 py-3 text-center">
                  <p className="num-lg text-4xl">{saved.points}</p>
                  <p className="label mb-0 mt-1">{t('practice.points')}</p>
                  <p className="mt-2 text-xs text-white/50">
                    {t('practice.best')} {saved.highestBreak}
                  </p>
                  <p className="mt-1 text-[0.7rem] text-white/35">
                    {token === null ? t('practice.notSaved') : t('practice.saved')}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Keep the essential action separate from the optional player name,
              so a long name cannot truncate "Start practice". */}
          <div className="mx-auto flex min-h-[7rem] w-full max-w-[clamp(28.2rem,42vw,36rem)] shrink-0 flex-col justify-center gap-2 overflow-hidden py-2">
            {token === null && (
              <p className="min-h-12 shrink-0 text-center text-xs leading-4 text-white/50">
                {t('practice.guestHint')}
              </p>
            )}

            <button
              type="button"
              onClick={() => void begin()}
              disabled={starting}
              className="btn shrink-0 bg-white px-4 py-4 text-lg text-hud-bg"
            >
              <span className="block">{starting ? t('practice.starting') : t('practice.start')}</span>
              <span className="block truncate text-sm">{name}</span>
            </button>

            {error && (
              <p className="line-clamp-2 shrink-0 text-center text-sm text-ball-red">{error}</p>
            )}
          </div>
        </main>
      </div>
    );
  }

  /**
   * Undo and finish, drawn once and used twice: on the right of the action bar
   * where they are pressed, and again as an invisible spacer on the left that
   * balances the bar so the rack sits on the centre of the board. Rendering
   * the same element means the spacer can never drift out of step with the
   * keys it is standing in for.
   */
  const actionKeys = (
    <>
      <PracticeKey
        label={t('match.undo')}
        icon={<UndoIcon className="h-[1.35rem] w-[1.35rem]" />}
        rgb="64,203,224"
        disabled={scoringDisabled || pots.length === 0}
        onClick={() => {
          if (operating.current) return;
          if (backing) void remote.undoLast();
          else setPots((p) => p.slice(0, -1));
        }}
      />
      <PracticeKey
        label={t('practice.finish')}
        icon={<CrownIcon className="h-[1.35rem] w-[1.35rem]" />}
        rgb="48,209,88"
        onClick={() => void finish()}
        disabled={locked}
        wide
      />
    </>
  );

  return (
    <div className="board-viewport">
      {aiNotice && <AiNotice message={aiNotice} onClose={() => setAiNotice(null)} />}
      <main
        className="board-shell live-shell mx-auto flex w-full max-w-[min(140rem,95vw)] flex-col gap-2 p-2 sm:gap-3 sm:p-3"
      >
        <ControlBar
          mode={expired ? 'manual' : state?.mode ?? 'manual'}
          streaming={!expired && (youtube.device
            ? ['preparing', 'waiting_for_camera', 'live'].includes(youtube.device.status)
            : state?.streaming ?? false)}
          streamingPending={locked || state?.status === 'match_over'}
          aiUnavailableReason={locked || state?.status === 'match_over' ? t('practice.starting')
            : cameras?.data?.devices.some(device => device.aiSupported === false) ? t('match.aiUnsupported') : null}
          clock={<><span className="hud-label">{t('practice.title')}</span> <Countdown deadline={deadline} /></>}
          leaveLabel={t('leave.practiceConfirm')}
          onModeChange={mode => void cameraChange('ai', mode !== 'manual')}
          onStreamingChange={enabled => void cameraChange('streaming', enabled)}
          onLeave={() => { if (!operating.current) setLeaving(true); }}
        />

        {/* The scoring board's player panel, with one player in it: same
            frame, same name plate, same oversized score, same break log and
            the same record in the footer. A practice session is a player at a
            table, so it is drawn as the board draws a player at a table
            rather than as a row of tiles that exists nowhere else. */}
        <div className="practice-live flex flex-1 gap-2 sm:gap-3">
          <div
            className="hud-frame flex min-h-0 flex-1 flex-col items-center gap-3 p-5 transition"
            style={frameVars(accent.rgb, true) as CSSProperties}
          >
            {/* Where the "at table" badge sits on the board. In practice
                nobody is waiting for the table, so the band carries the one
                figure that badge would otherwise crowd out: the break in
                progress. It empties, rather than showing a nought, so the
                panel is quiet between breaks. */}
            <div className="flex h-7 w-full shrink-0 items-center justify-center">
              {current > 0 && (
                <span className="turn-badge" style={{ '--turn-rgb': accent.rgb } as CSSProperties}>
                  <span className="turn-dot" />
                  {t('practice.break')} {current}
                </span>
              )}
            </div>

            <div
              className="hud-frame hud-frame-sm max-w-full px-6 py-2"
              style={QUIET_FRAME as CSSProperties}
            >
              <span
                className="block truncate text-center text-2xl font-semibold leading-tight
                           tracking-[0.01em] text-white sm:text-[2rem]"
              >
                {name}
              </span>
            </div>

            <div className="fit-box w-full min-h-0 flex-1 rounded-3xl">
              <span
                className="num-lg block leading-none"
                style={{
                  fontSize: 'min(64cqh, 40cqw)',
                  color: accent.hex,
                  letterSpacing: '-0.04em',
                }}
              >
                {String(pointsScored).padStart(2, '0')}
              </span>
            </div>

            {/* Every ball potted, in order, as the board draws them: the same
                chips at the same size, with each finished break totalled where
                it ended. A list of break totals said a player made 24; this
                says red, black, red, blue, which is the thing worth keeping
                from a practice session and the thing that lets a mis-tap be
                spotted while it is still the last ball. */}
            <div className="h-[5.2rem] w-full shrink-0">
              <BreakHistory visits={visits} player={0} />
            </div>

            <div
              aria-hidden
              className="h-px w-full shrink-0"
              style={{
                background: `linear-gradient(to right, transparent, rgba(${accent.rgb},0.4) 15%, rgba(${accent.rgb},0.4) 85%, transparent)`,
              }}
            />

            <div className="flex w-full shrink-0 items-center justify-center gap-2.5 pt-2">
              <TrophyIcon
                className={`h-5 w-5 shrink-0 sm:h-6 sm:w-6 ${
                  highestBreak > 0 ? 'text-sys-yellow/75' : 'text-white/40'
                }`}
              />
              <span className="hud-label">{t('match.highLabel')}</span>
              <span
                className={`num text-lg font-semibold leading-none sm:text-xl ${
                  highestBreak > 0 ? 'text-sys-yellow' : 'text-white/80'
                }`}
              >
                {highestBreak}
              </span>
            </div>
          </div>
        </div>

        {/* The scoring board's action bar, with the keys practice actually
            has. The rack is the board's rack — same constants, same gloss,
            same numerals across the balls — because a brown that is one shade
            in a match and another in practice is the same ball drawn twice.
            Every ball is live here: free practice has no ball that is "on", so
            nothing is dimmed and nothing is refused.

            The rack takes the room to the left of the rule and centres itself
            in it. It had a spacer on the far left for a while, to centre the
            balls on the panel rather than on their own half of the bar, and
            the balls paid for it: they are sized against the width of the box
            that holds them, so reserving a keys' worth of it made every ball
            visibly smaller. A rack of full-size balls in half a bar beats a
            rack of small ones centred on the whole of it. */}
        <div className="action-bar hud-frame flex min-h-[clamp(5.6rem,15vh,9.4rem)] flex-none flex-wrap items-stretch gap-3 px-3 py-2 sm:gap-4 lg:flex-nowrap">
          <div className="ball-row flex min-h-0 min-w-0 basis-full flex-1 items-center justify-center gap-0 lg:basis-auto">
            {(['red', ...COLORS] as BallColor[]).map((color) => (
              <button
                key={color}
                type="button"
                disabled={scoringDisabled}
                onClick={() => {
                  if (operating.current) return;
                  if (backing) void remote.dispatch({ type: 'shot.pot', source: 'manual', player: 0, color, count: 1, freeBall: false });
                  else setPots((p) => [...p, color]);
                }}
                title={t(ballKey(color))}
                aria-label={`${t(ballKey(color))} ${BALL_VALUES[color]}`}
                className="flex min-h-0 shrink-0 items-center justify-center transition active:scale-95"
              >
                <span
                  aria-hidden
                  style={{
                    ...BALL_GLOSS,
                    filter: 'drop-shadow(0 0.05em 0.12em rgba(0,0,0,0.6)) brightness(1.06)',
                  }}
                  className={`ball-fit grid shrink-0 place-items-center rounded-full transition ${BALL_SWATCH[color]}`}
                >
                  <span
                    className={`num text-[0.42em] font-semibold leading-none ${
                      PALE_BALLS.has(color) ? 'text-black/65' : 'text-white/90'
                    }`}
                    style={{
                      textShadow: PALE_BALLS.has(color)
                        ? '0 0.03em 0.05em rgba(255,255,255,0.45)'
                        : '0 0.03em 0.06em rgba(0,0,0,0.55)',
                    }}
                  >
                    {BALL_VALUES[color]}
                  </span>
                </span>
              </button>
            ))}
          </div>

          <span aria-hidden className="my-1 hidden w-px shrink-0 self-stretch bg-white/10 lg:block" />

          <div className="practice-keys flex w-full shrink-0 items-center justify-center gap-2 sm:gap-2.5 lg:mr-4 lg:w-auto lg:justify-start">
            {actionKeys}
          </div>
        </div>

        {(error || remote.error || youtube.error || cameras?.error) && <p role="alert" className="shrink-0 text-center text-sm text-ball-red">{error || remote.error || youtube.error || cameras?.error}</p>}

        {leaving && (
          <ConfirmDialog
            title={t('leave.practiceTitle')}
            message={t('leave.practiceMessage')}
            confirmLabel={t('leave.practiceConfirm')}
            onConfirm={() => void leave()}
            onCancel={() => setLeaving(false)}
          />
        )}
      </main>
    </div>
  );
}

/**
 * One console key, matching the scoring board's.
 *
 * Fixed slots use the same measured labels as match keys, so longer
 * translations cannot change the rack width or the action bar height.
 */
function PracticeKey({
  label,
  icon,
  rgb,
  onClick,
  disabled = false,
  wide = false,
}: {
  label: string;
  icon: ReactNode;
  rgb: string;
  onClick: () => void;
  disabled?: boolean;
  wide?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`hud-frame flex items-center justify-center gap-2 px-3 py-1.5 sm:py-2 transition
                  hover:brightness-125 active:scale-95 disabled:cursor-not-allowed
                  disabled:opacity-35 sm:gap-2.5 sm:px-4 ${
                    wide ? 'practice-finish' : 'practice-undo'
                  }`}
      style={{
        ['--hud-c' as string]: '999px',
        ['--hud-edge' as string]: '8px',
        ['--hud-bright' as string]: `rgba(${rgb},0.22)`,
        ['--hud-glow' as string]: 'transparent',
        ['--hud-fill' as string]: `rgba(${rgb},0.03)`,
        color: `rgb(${rgb})`,
      }}
    >
      <span className="shrink-0 opacity-90">{icon}</span>
      <FitLabel text={label} />
    </button>
  );
}

/**
 * The pot list as the board's visits, so the break log can be drawn by the
 * same component the match uses.
 *
 * Consecutive pots of one colour are collapsed into a count, exactly as the
 * match reducer does it: eight reds in a row are one chip reading `8x1`, not
 * eight identical circles the eye has to count. Every break but the one in
 * progress is marked ended, which is what puts its total on the end of the run.
 */
function toVisits(pots: (BallColor | 'end')[]): Visit[] {
  const visits: Visit[] = [];
  let entries: VisitEntry[] = [];
  let points = 0;

  const close = (ended: boolean): void => {
    if (entries.length === 0) return;
    visits.push({ player: 0, entries, points, ended });
    entries = [];
    points = 0;
  };

  for (const pot of pots) {
    if (pot === 'end') {
      close(true);
      continue;
    }
    const last = entries[entries.length - 1];
    if (last && last.kind === 'pot' && last.color === pot) {
      last.count += 1;
      last.points += BALL_VALUES[pot];
    } else {
      entries.push({ kind: 'pot', color: pot, count: 1, points: BALL_VALUES[pot] });
    }
    points += BALL_VALUES[pot];
  }
  close(false);

  return visits;
}

/** Completed breaks, in order. */
function toBreaks(pots: (BallColor | 'end')[]): number[] {
  const done: number[] = [];
  let running = 0;
  for (const pot of pots) {
    if (pot === 'end') {
      if (running > 0) done.push(running);
      running = 0;
    } else {
      running += BALL_VALUES[pot];
    }
  }
  return done;
}

function currentBreak(pots: (BallColor | 'end')[]): number {
  let running = 0;
  for (const pot of pots) {
    if (pot === 'end') running = 0;
    else running += BALL_VALUES[pot];
  }
  return running;
}
